import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/cc";
import { Bell, PlayCircle, RefreshCw, Mail, MessageSquare, Clock } from "lucide-react";

const STAGE_META = {
  pending_24h: { label: "Pending confirmation · 24h", channel: "email", icon: Mail },
  pending_48h: { label: "Pending confirmation · 48h", channel: "sms", icon: MessageSquare },
  pending_72h: { label: "Pending confirmation · 72h", channel: "email", icon: Mail },
  before_7d:   { label: "Scheduled · 7 days before",  channel: "email", icon: Mail },
  before_1d:   { label: "Scheduled · 1 day before",   channel: "sms",   icon: MessageSquare },
  before_2h:   { label: "Scheduled · 2 hours before", channel: "both",  icon: Bell },
};

export default function AdminRemindersPage() {
  const { user: me } = useAuth();
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [log, setLog] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [logRes, pendRes] = await Promise.all([
        api.get("/admin/reminders/log", { params: { limit: 25 } }),
        api.get("/admin/reminders/pending"),
      ]);
      setLog(logRes.data || []);
      setPending(pendRes.data || []);
    } catch {
      toast.error("Failed to load reminders data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/reminders/run-now");
      setLastRun(data);
      const total = Object.values(data || {}).reduce((a, b) => a + (b || 0), 0);
      toast.success(total ? `Sent ${total} reminder${total === 1 ? "" : "s"}` : "No reminders due right now");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  if (me?.role !== "super_admin" && me?.role !== "hr_admin") {
    return <div className="cc-card p-8 text-[var(--cc-text-soft)]">Insufficient permissions.</div>;
  }

  return (
    <div className="fade-up" data-testid="admin-reminders-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow mb-2">Communications</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3">
            <Bell className="w-8 h-8" />Interview Reminders
          </h1>
          <p className="text-[var(--cc-text-soft)] mt-1">
            Automatic cron runs every 15 min. Use the button below to dispatch any due reminders on demand.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={refresh} disabled={loading} data-testid="reminders-refresh-button">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button className="btn-primary" onClick={runNow} disabled={running} data-testid="reminders-run-now-button">
            <PlayCircle className="w-4 h-4" /> {running ? "Sending…" : "Send Reminders Now"}
          </button>
        </div>
      </div>

      {/* Schedule overview */}
      <div className="cc-card p-5 mb-6">
        <div className="label-eyebrow mb-3">Reminder schedule</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          {Object.entries(STAGE_META).map(([k, m]) => {
            const Icon = m.icon;
            return (
              <div key={k} className="flex items-center gap-3 p-3 rounded-lg border border-[var(--cc-border)] bg-[var(--cc-surface)]">
                <Icon className="w-4 h-4 text-[var(--cc-primary)]" />
                <div>
                  <div className="font-medium">{m.label}</div>
                  <div className="text-xs text-[var(--cc-text-soft)] capitalize">via {m.channel}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Last manual run */}
      {lastRun && (
        <div className="cc-card p-5 mb-6" data-testid="reminders-last-run">
          <div className="label-eyebrow mb-3">Last manual run</div>
          <div className="flex flex-wrap gap-2 text-sm">
            {Object.entries(lastRun).map(([stage, count]) => (
              <span key={stage} className={`pill ${count > 0 ? "pill-success" : "pill-neutral"}`}>
                {STAGE_META[stage]?.label || stage}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Pending */}
      <div className="cc-card overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-[var(--cc-border)] flex items-center gap-2">
          <Clock className="w-4 h-4 text-[var(--cc-text-soft)]" />
          <div className="label-eyebrow">Upcoming / due reminders ({pending.length})</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">Candidate</th>
              <th className="px-5 py-3 label-eyebrow">Job</th>
              <th className="px-5 py-3 label-eyebrow">Status</th>
              <th className="px-5 py-3 label-eyebrow">Scheduled at</th>
              <th className="px-5 py-3 label-eyebrow">Pending stages</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((row) => (
              <tr key={row.candidate_id} className="border-t border-[var(--cc-border)]" data-testid={`reminders-row-${row.candidate_id}`}>
                <td className="px-5 py-3">
                  <div className="font-medium">{row.candidate_name}</div>
                  <div className="text-xs text-[var(--cc-text-soft)]">{row.candidate_email}</div>
                </td>
                <td className="px-5 py-3">{row.job_title || "—"}</td>
                <td className="px-5 py-3">
                  <span className="pill pill-info">{row.interview_status}</span>
                </td>
                <td className="px-5 py-3 text-xs">{row.scheduled_at ? fmtDateTime(row.scheduled_at) : "—"}</td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1">
                    {row.pending_reminders.map((r) => (
                      <span key={r.stage} className="pill pill-neutral text-[10px]">
                        {STAGE_META[r.stage]?.label || r.stage}
                        {r.due_in_hours !== undefined && (
                          <span className="ml-1 opacity-60">{r.due_in_hours > 0 ? `in ${r.due_in_hours}h` : `${Math.abs(r.due_in_hours)}h overdue`}</span>
                        )}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {pending.length === 0 && (
              <tr><td colSpan="5" className="text-center py-10 text-[var(--cc-text-soft)]">No pending reminders right now.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* History */}
      <div className="cc-card overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--cc-border)]">
          <div className="label-eyebrow">Recent runs</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">When</th>
              {Object.entries(STAGE_META).map(([k, m]) => (
                <th key={k} className="px-5 py-3 label-eyebrow text-xs">{m.label.split(" · ")[1] || k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {log.map((r) => (
              <tr key={r.id} className="border-t border-[var(--cc-border)]" data-testid={`reminders-log-${r.id}`}>
                <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)] whitespace-nowrap">{fmtDateTime(r.at)}</td>
                {Object.keys(STAGE_META).map((k) => (
                  <td key={k} className="px-5 py-3 text-xs">
                    {r.fired?.[k] > 0 ? <span className="pill pill-success">{r.fired[k]}</span> : <span className="text-[var(--cc-text-soft)]">0</span>}
                  </td>
                ))}
              </tr>
            ))}
            {log.length === 0 && (
              <tr><td colSpan={Object.keys(STAGE_META).length + 1} className="text-center py-10 text-[var(--cc-text-soft)]">No reminder runs logged yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
